import React from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import api from '../../../middleware/api';
import { useAuth } from '../../../context/AuthContext';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useOutlet, useOutletContext } from 'react-router-dom';
const validationCreateOrderSchema = Yup.object().shape({
    // Champs principaux
    userId: Yup.number().required('User ID is required'),
    customerId: Yup.number().integer().required('Customer ID is required'),
    capsuleIds: Yup.array().of(Yup.number().integer()).required('Capsule IDs are required'),
    isFree: Yup.boolean().required('Le prix est requis'),
    shippingType: Yup.string().oneOf(['standard', 'customer'], 'Le type de livraison est requis'),

    // Nouveaux champs pour la logique des propriétaires
    shippingOwnerId: Yup.number().integer().required('Shipping owner ID is required'),
    billingOwnerId: Yup.number().integer().required('Billing owner ID is required'),

    // Validation conditionnelle des IDs d'adresses
    shippingAddressId: Yup.number().nullable()
        .when('shippingType', {
            is: 'customer',
            then: (schema) => schema.test(
                'customer-address-validation',
                'Une adresse de livraison valide est requise pour un envoi au client',
                function (value) {
                    // Si pas d'ID, on crée une nouvelle adresse (OK)
                    // Si ID fourni, il doit être valide
                    return value === null || (typeof value === 'number' && value > 0);
                }
            ),
            otherwise: (schema) => schema.nullable()
        }),

    billingAddressId: Yup.number().nullable()
        .test(
            'therapist-billing-validation',
            'Une adresse de facturation du thérapeute est requise',
            function (value) {
                // L'adresse de facturation est toujours celle du thérapeute
                return value === null || (typeof value === 'number' && value > 0);
            }
        ),

    // Validation des adresses
    addresses: Yup.object().shape({
        shipping: Yup.object().shape({
            address_1: Yup.string().required('Adresse est requise'),
            address_2: Yup.string().nullable().default(''),
            city: Yup.string().required('Ville est requise'),
            postcode: Yup.string().required('Code Postal est requis'),
            state: Yup.string().nullable().default(''),
            country: Yup.string().required('Pays est requis'),
            first_name: Yup.string().required('Prénom est requis'),
            last_name: Yup.string().required('Nom est requis'),
            phone: Yup.string().nullable().default(''),
            email: Yup.string().email('Email invalide').nullable().default(''),
            // Validation conditionnelle du représentant légal
            legal_first_name: Yup.string().when('$useLegalRepresentative', {
                is: true,
                then: (schema) => schema.required('Prénom du représentant légal est requis'),
                otherwise: (schema) => schema.nullable()
            }),
            legal_last_name: Yup.string().when('$useLegalRepresentative', {
                is: true,
                then: (schema) => schema.required('Nom du représentant légal est requis'),
                otherwise: (schema) => schema.nullable()
            })
        }),
        billing: Yup.object().shape({
            address_1: Yup.string().required('Adresse de facturation est requise'),
            address_2: Yup.string().nullable().default(''),
            city: Yup.string().required('Ville de facturation est requise'),
            postcode: Yup.string().required('Code Postal de facturation est requis'),
            state: Yup.string().nullable().default(''),
            country: Yup.string().required('Pays de facturation est requis'),
            first_name: Yup.string().required('Prénom de facturation est requis'),
            last_name: Yup.string().required('Nom de facturation est requis'),
            phone: Yup.string().nullable().default(''),
            email: Yup.string().email('Email invalide').nullable().default('')
        }),
    }).required('Les adresses sont requises'),

    // Validation du représentant légal
    legalRepresentative: Yup.object().shape({
        firstName: Yup.string().nullable(),
        lastName: Yup.string().nullable()
    }).nullable()
});

const CapsuleOrderContext = createContext();

const CapsuleOrderProvider = ({ children, capsuleIds }) => {

    const { setUser } = useAuth();
    const [product, setProduct] = useState(null);
    const [capsules, setCapsules] = useState([]);
    const [therapist, setTherapist] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { customer: outletCustomer, setData: setOutletCustomer } = useOutletContext()
    const createOrder = async (newOrder) => {
        try {
            validationCreateOrderSchema.validateSync(newOrder, { abortEarly: false });
        } catch (validationErrors) {
            console.error("Validation errors:", validationErrors.inner);
            validationErrors.inner.forEach(err => {
                toast.error(`Erreur de validation: ${err.path} - ${err.message}`);
            });
            throw new Error('Validation errors');
        }
        try {
            const response = await api.post("/orders/capsules", newOrder);
            if (response.data?.success) {
                const successMessage = newOrder.capsuleIds.length > 1 ? "Capsules commandées avec succès !" : "Capsule commandée avec succès !";
                handleUpdateOutletCustomerOrders(response.data.order, response.data.newBalance)
                return { success: true, message: successMessage, order: response.data.order }
            } else {
                return { success: false, message: response.data?.message ? response.data.message : 'Erreur lors de la commande' }
            }
        } catch (e) {
            console.error(e);
            return { success: false, message: e.response?.data?.message || 'Erreur lors de la commande, veuillez réessayer' }
        }
    }

    const handleUpdateOutletCustomerOrders = async (newOrder, newBalance) => {


        const relatedCapsuleIds = newOrder.relatedCapsules.map((capsule) => parseInt(capsule.id))

        const formatedOrder = {
            creationDate: newOrder.creationDate,
            customerId: newOrder.customerId,
            id: newOrder.id,
            relatedCapsuleIds: relatedCapsuleIds,
            status: newOrder.status
        }


        setOutletCustomer((prev) => {
            return {
                ...prev,
                customerOrders: [...prev.customerOrders, formatedOrder],
                addresses: (newOrder?.customer && newOrder?.customer?.addresses) ? [
                    ...newOrder?.customer?.addresses
                ] : prev.addresses
            }
        })


        setUser((prev) => {
            return {
                ...prev,
                currentCredits: prev.currentCredits >= newBalance ? newBalance : prev.currentCredits
            }
        })

    }
    useEffect(() => {

        const fetchProduct = async () => {
            try {
                setLoading(true)
                const response = await api.get(`/products/${13395}`);
                if (response.data.success) {
                    setProduct(response.data.product);
                } else {
                    console.error("Erreur lors de la récupération du produit :", response.data.message);
                }
            } catch (err) {
                console.error("Erreur lors de la récupération du produit :", err);
            } finally {
                setLoading(false)
            }
        };

        const fetchCapsuleData = async () => {
            try {
                setLoading(true)
                console.log(capsuleIds)
                const results = await Promise.all(
                    capsuleIds.map((id) => api.get(`/capsules/order-information/${id}`))
                );
                const capsuleArray = results.map((res) => res.data.capsule)
                const therapist = results[0].data.therapist
                const customer = results[0].data.customer
                console.log(therapist)
                setCapsules(capsuleArray)
                setTherapist(therapist)
                setCustomer(customer)

            } catch (err) {
                setError(err);
            } finally {
                setLoading(false)
            }
        };

        fetchProduct();
        fetchCapsuleData();
    }, []); // Fetch product only once


    const value = {
        product,
        createOrder,
        capsules,
        therapist,
        customer,
        loading,
        error
    }

    return (
        <CapsuleOrderContext.Provider value={value}>
            {children}
        </CapsuleOrderContext.Provider>
    )
}

export const useCapsuleOrder = () => {
    const context = useContext(CapsuleOrderContext);

    if (!context) {
        throw new Error('useCapsuleOrder must be used within a CapsuleOrderProvider');
    }
    return context
}

export default CapsuleOrderProvider;
